import { useMemo } from "react";

import type { ICampaignsScrapingConfiguration } from "app/components/campaigns/shared/CampaignsConst";
import type { IKeywordsReportRecordManagedData } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportTypes";
import type { IKeywordsCampaignsTableTopRemoveKeywordsModalProps } from "app/pages/digital-marketing/keywords-campaigns/wizard/wizard-pages/adding-keywords-step/keywords-table/types";
import { AllSelectedVariant } from "app/pages/digital-marketing/keywords-campaigns/wizard/wizard-pages/adding-keywords-step/keywords-table/types";

/**
 * Return props based on selected keywords and current scraping configurations to display
 * a blocking message when keywords are selected for at least one or all scraping configurations.
 * @param scrapingConfigurations
 * @param selectedRows
 */
export const useRemoveKeywordsModalSelectionProps = (
    scrapingConfigurations: ICampaignsScrapingConfiguration[],
    selectedRows: IKeywordsReportRecordManagedData[],
): Pick<
    IKeywordsCampaignsTableTopRemoveKeywordsModalProps,
    "presentErrorOnAllSelected" | "tableRecordsLength" | "allSelectedVariant"
> => {
    return useMemo(() => {
        // Total count of keywords for the current matched scraping configurations
        const totalKeywordsCount = scrapingConfigurations.reduce((sum, config) => sum + config.keywords.length, 0);
        const selectedRowsLength = selectedRows?.length;

        if (scrapingConfigurations.length === 0 || selectedRowsLength === 0) {
            return {
                presentErrorOnAllSelected: false,
                allSelectedVariant: undefined,
                tableRecordsLength: totalKeywordsCount,
            };
        }

        const selectedCountByConfigId: Record<string, number> = {};
        selectedRows.forEach((row) => {
            const configId = row.scrapingConfigurationId;
            if (!configId) {
                return;
            }
            const currentCount = selectedCountByConfigId[configId] ?? 0;
            selectedCountByConfigId[configId] = currentCount + 1;
        });

        let fullySelectedConfigs = 0;
        scrapingConfigurations.forEach((config) => {
            const selectedForConfig = selectedCountByConfigId[config.id] ?? 0;
            if (config.keywords.length > 0 && selectedForConfig === config.keywords.length) {
                fullySelectedConfigs += 1;
            }
        });

        const hasAnyFullySelected = fullySelectedConfigs > 0;
        let allSelectedVariant: AllSelectedVariant;
        if (hasAnyFullySelected) {
            const allConfigsFullySelected =
                hasAnyFullySelected && fullySelectedConfigs === scrapingConfigurations.length;
            allSelectedVariant = allConfigsFullySelected
                ? AllSelectedVariant.BlockAllKeywords
                : AllSelectedVariant.DeleteSearchEngine;
        }

        return {
            presentErrorOnAllSelected: hasAnyFullySelected,
            allSelectedVariant,
            tableRecordsLength: hasAnyFullySelected ? selectedRowsLength : totalKeywordsCount,
        };
    }, [scrapingConfigurations, selectedRows]);
};
